import { Link } from "react-router-dom";
import { WidgetCard, EmptyState } from "@steelyard/theme";
import { Button, Alert, Text } from "rizzui";
import { customerPath } from "../../lib/paths";

/**
 * Ported from apps/admin/components/customer-dedupe-report.tsx. There is no merge button, and
 * there is not a disabled one either — §14.4 refuses automatic merging on purpose.
 *
 * T025/T026 (010): one report, two sources merged into the same shape (FR-021, SC-006). A
 * collision is a **group** of two or more colliding customer ids, not a pair — the Customers
 * service's own `DedupeCollision.CustomerIds` is not always length 2 — so `DedupeGroup` carries a
 * plain id list rather than the earlier `left`/`right` two-record model, which never matched
 * either backend's real wire shape in the first place (both APIs return bare customer GUIDs and a
 * `matchKey`/`collidedOn` string, never a `left`/`right`/`customers` object or a `field` key).
 */
export type DedupeGroup = {
  /** Stable React key — index-qualified because two different sources can otherwise share ids/field. */
  key: string;
  /** Canonical label key: "gstin" | "registered_mobile" | "legal_name" — see `label()` below. */
  field: string;
  customerIds: string[];
  /** The service's own name for each id, same order; `null` where it could not say. Never the id. */
  customerNames?: (string | null)[];
  /** Customers' `CUS-000123` for each id, same order — the link key; `null` where absent (Tally's groups). */
  customerNumbers?: (string | null)[];
  /** Only ever true for a portal-sourced group — the Tally response carries no such field (FR-019). */
  divergesFromAccounting: boolean;
  source: "portal" | "tally";
};

const FIELD_LABEL: Record<string, string> = {
  gstin: "GSTIN",
  registered_mobile: "registered mobile",
  mobile: "registered mobile",
  legal_name: "legal name",
  normalised_name: "legal name",
};

function label(field: string): string {
  return FIELD_LABEL[field] ?? field;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * `GET /api/customers/admin/dedupe` → `{ collisions: [{ matchKey, customerIds, divergesFromAccounting }] }`
 * (`CustomerAdminQueries.CollisionsAsync` / `DedupeCollision`, read from source — the OpenAPI
 * document declares no response body for this operation). Declares rather than casts, same
 * discipline as `parseSyncQueue`.
 */
export function parsePortalCollisions(body: unknown): { groups: DedupeGroup[]; unreadable: number } {
  const raw = isRecord(body) ? body.collisions : undefined;

  if (!Array.isArray(raw)) {
    throw new Error(
      "GET /api/customers/admin/dedupe answered without a collisions array. Rendering the report empty would " +
        "say there are no colliding records, which is a different fact from not being able to read the answer.",
    );
  }

  const groups: DedupeGroup[] = [];
  let unreadable = 0;

  raw.forEach((entry, index) => {
    const row = isRecord(entry) ? entry : {};
    const field = row.matchKey;
    const ids = Array.isArray(row.customerIds) ? row.customerIds.filter((id): id is string => typeof id === "string") : [];
    // `customers: [{ id, name, customerNumber }]` rides beside the ids (legal name, else display name).
    const labels = Array.isArray(row.customers) ? row.customers.filter(isRecord) : [];
    const fieldOf = (id: string, key: "name" | "customerNumber") => {
      const value = labels.find((label) => label.id === id)?.[key];
      return typeof value === "string" ? value : null;
    };

    if (typeof field !== "string" || ids.length < 2) {
      unreadable += 1;
      return;
    }

    groups.push({
      key: `portal-${index}-${ids.join("-")}`,
      field,
      customerIds: ids,
      customerNames: ids.map((id) => fieldOf(id, "name")),
      customerNumbers: ids.map((id) => fieldOf(id, "customerNumber")),
      divergesFromAccounting: row.divergesFromAccounting === true,
      source: "portal",
    });
  });

  return { groups, unreadable };
}

/**
 * `GET /api/tallysync/admin/dedupe` → `{ collisions: [{ customerIdA, customerIdB, collidedOn,
 * detectedAt, resolved }] }` (`TallyAdminQueries.CollisionsAsync` / `CollisionRow`, read from
 * source — also undeclared in the OpenAPI document). Always a pair on this side, never a
 * `divergesFromAccounting` field — the Tally connector has no notion of a local override.
 */
export function parseTallyCollisions(body: unknown): { groups: DedupeGroup[]; unreadable: number } {
  const raw = isRecord(body) ? body.collisions : undefined;

  if (!Array.isArray(raw)) {
    throw new Error(
      "GET /api/tallysync/admin/dedupe answered without a collisions array. Rendering the report empty would " +
        "say there are no colliding records, which is a different fact from not being able to read the answer.",
    );
  }

  const groups: DedupeGroup[] = [];
  let unreadable = 0;

  raw.forEach((entry, index) => {
    const row = isRecord(entry) ? entry : {};
    const field = row.collidedOn;
    const a = row.customerIdA;
    const b = row.customerIdB;

    if (typeof field !== "string" || typeof a !== "string" || typeof b !== "string") {
      unreadable += 1;
      return;
    }

    const nameA = typeof row.customerNameA === "string" ? row.customerNameA : null;
    const nameB = typeof row.customerNameB === "string" ? row.customerNameB : null;
    groups.push({ key: `tally-${index}-${a}-${b}`, field, customerIds: [a, b], customerNames: [nameA, nameB], divergesFromAccounting: false, source: "tally" });
  });

  return { groups, unreadable };
}

export function CustomerDedupeReport({
  groups,
  unreadable = 0,
}: {
  groups: readonly DedupeGroup[];
  unreadable?: number;
}) {
  if (groups.length === 0 && unreadable === 0) {
    return (
      <EmptyState title="No collisions">
        No two customer records share a GSTIN, a registered mobile or a normalised legal name — on the portal or
        in Tally — and no record diverges from the accounting system after a local override.
      </EmptyState>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {unreadable > 0 ? (
        <div role="alert">
          <Alert color="danger" variant="flat">
            {unreadable} {unreadable === 1 ? "collision was" : "collisions were"} returned in a shape this screen
            could not read and {unreadable === 1 ? "is" : "are"} not listed below. They have not gone away —
            report this rather than treating the list as complete.
          </Alert>
        </div>
      ) : null}

      <ul className="flex flex-col gap-3">
        {groups.map((group) => (
          <li key={group.key}>
            <WidgetCard title={`Shared ${label(group.field)}`}>
              <div className="flex flex-col gap-3 pt-4">
                <div className="flex flex-wrap items-center gap-3">
                  {group.customerIds.map((id, index) => (
                    <RecordLink key={id} customerKey={group.customerNumbers?.[index] || id} displayName={group.customerNames?.[index] ?? null} />
                  ))}
                </div>
                {group.divergesFromAccounting ? (
                  <div role="alert">
                    <Alert color="warning" variant="flat">
                      This record diverges from the accounting record after a local override.
                    </Alert>
                  </div>
                ) : null}
                <Text className="text-muted-foreground text-xs">
                  Infra Junction does not merge customer records. Open whichever record is wrong and correct the{" "}
                  {label(group.field)}; the {group.customerIds.length === 2 ? "pair" : "group"} leaves this report
                  once they no longer collide.
                </Text>
              </div>
            </WidgetCard>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** `customerKey` is the customer number, or the id where none came (the customer route redirects an id). */
function RecordLink({ customerKey, displayName }: { customerKey: string; displayName?: string | null }) {
  const text = `Open ${displayName ?? "Unknown customer"}`;
  return (
    <Link to={customerPath(customerKey)}>
      <Button as="span" variant="outline" size="sm">
        {text}
      </Button>
    </Link>
  );
}
